import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Login.css';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyResetToken, resetPassword } = useAuth();

  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const token = searchParams.get('token');

  // Verify token on mount
  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        setTokenValid(false);
        setLoading(false);
        return;
      }

      const result = await verifyResetToken(token);
      setTokenValid(result.valid);
      if (!result.valid && result.error) {
        setError(result.error);
      }
      setLoading(false);
    };

    checkToken();
  }, [token, verifyResetToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setSubmitting(true);

    const result = await resetPassword(token!, newPassword);

    if (result.success) {
      setSuccess(true);
    } else {
      setError(result.error || 'Reset failed. Please try again.');
    }

    setSubmitting(false);
  };

  // Loading state
  if (loading) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <img src="/hugemajicrealmlogo.png" alt="The Majic Realm" className="login-logo" />
            <p>Verifying reset link...</p>
          </div>
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  // Invalid token
  if (!tokenValid) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <img src="/hugemajicrealmlogo.png" alt="The Majic Realm" className="login-logo" />
            <p>Reset Password</p>
          </div>

          <div className="error-message">
            <p>{error || 'Invalid or expired reset link'}</p>
            <p className="error-note">Reset links expire after 20 minutes. Please request a new one.</p>
          </div>

          <Link to="/login" className="submit-button" style={{ textAlign: 'center', display: 'block', textDecoration: 'none' }}>
            Request New Reset Link
          </Link>

          <div className="login-footer">
            <Link to="/" className="back-link">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <img src="/hugemajicrealmlogo.png" alt="The Majic Realm" className="login-logo" />
            <p>Password Reset Complete</p>
          </div>

          <div className="success-message">
            <p>Your password has been reset successfully!</p>
            <p className="success-note">You can now log in with your new password.</p>
          </div>

          <button
            type="button"
            className="submit-button"
            onClick={() => navigate('/login')}
          >
            Go to Login
          </button>

          <div className="login-footer">
            <Link to="/" className="back-link">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <img src="/hugemajicrealmlogo.png" alt="The Majic Realm" className="login-logo" />
          <p>Create a new password</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="newPassword"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setError('');
                }}
                required
                placeholder="Enter new password"
                minLength={6}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            <small className="form-hint">Password must be at least 6 characters</small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setError('');
                }}
                required
                placeholder="Confirm new password"
                minLength={6}
              />
            </div>
          </div>

          <button
            type="submit"
            className="submit-button"
            disabled={submitting || !newPassword || !confirmPassword}
          >
            {submitting ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <div className="login-footer">
          <Link to="/login" className="back-link">
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
